import {
  createBrowserRouter,
  Outlet,
  type RouteObject,
  ScrollRestoration,
  useMatches,
} from "react-router";
import { SessionGate } from "@/features/auth/components/SessionGate";
import { AdvisorGate } from "@/features/auth/components/AdvisorGate";
import { AppShell, type ShellRouteHandle } from "@/app/workspace/AppShell";
import { TimeZoneProvider } from "@/shared/time/TimeZoneProvider";
import { DockGate } from "@/app/workspace/DockGate";
import { RouteTransition } from "@/app/workspace/RouteTransition";
import { RouteErrorFallback } from "@/app/errors/RouteErrorFallback";
import { RouteErrorPage } from "@/app/errors/RouteErrorPage";

import { SignInPage } from "@/features/auth/components/SignInPage";

export const routes: RouteObject[] = [
  {
    errorElement: <RouteErrorPage />,
    children: [
      { path: "/auth/signin", element: <SignInPage /> },
      {
        path: "/auth/signed-out",
        lazy: async () => ({
          Component: (await import("@/app/auth/LoggedOutPage")).LoggedOutPage,
        }),
      },
      {
        path: "/auth/representation",
        lazy: async () => {
          const { RepresentationPage } = await import(
            "@/features/auth/components/RepresentationPage"
          );
          return {
            element: (
              <SessionGate>
                <RepresentationPage />
              </SessionGate>
            ),
          };
        },
      },
      {
        element: <AppLayout />,
        children: [
          {
            errorElement: <RouteErrorPage />,
            children: [
              {
                index: true,
                lazy: async () => ({
                  Component: (await import("@/features/home/components/HomePage")).HomePage,
                }),
              },
              {
                path: "clients",
                handle: title("shell.titles.clients"),
                lazy: async () => ({
                  Component: (await import("@/features/clients/components/ClientsPage"))
                    .ClientsPage,
                }),
              },
              {
                path: "clients/:id",
                handle: title("shell.titles.clients"),
                lazy: async () => ({
                  Component: (await import("@/routes/ClientDetailRoute")).ClientDetailRoute,
                }),
              },
              {
                path: "clients/:id/proposal",
                handle: title("shell.titles.proposal"),
                lazy: async () => ({
                  Component: (await import("@/features/proposal/components/ProposalPage"))
                    .ProposalPage,
                }),
              },
              {
                path: "chat",
                handle: { conversationOnly: true } satisfies ShellRouteHandle,
                lazy: async () => ({
                  Component: (await import("@/features/intelligence/components/ChatDeepLink"))
                    .ChatDeepLink,
                }),
              },
              {
                path: "chats",
                handle: title("shell.titles.chats"),
                lazy: async () => ({
                  Component: (await import("@/features/intelligence/components/ChatsListPage"))
                    .ChatsListPage,
                }),
              },
              {
                path: "settings/:tab?",
                handle: { ...title("shell.titles.settings"), keepMountedAcrossSegments: true },
                lazy: async () => ({
                  Component: (await import("@/features/settings/components/SettingsPage"))
                    .SettingsPage,
                }),
              },
              {
                path: "fa/:tab?",
                handle: { ...title("shell.titles.fa"), keepMountedAcrossSegments: true },
                lazy: async () => ({
                  Component: (await import("@/routes/FaRoute")).FaRoute,
                }),
              },
              {
                path: "advice-portal",
                handle: title("shell.titles.documents"),
                lazy: async () => ({
                  Component: (await import("@/features/documents/components/DocumentsPage"))
                    .DocumentsPage,
                }),
              },
              {
                path: "advice-portal/new-advice",
                handle: title("shell.titles.advicePortal"),
                lazy: async () => ({
                  Component: (await import("@/routes/NewAdviceRoute")).NewAdviceRoute,
                }),
              },
              {
                path: "advice-portal/new-client",
                handle: title("shell.titles.advice"),
                lazy: async () => ({
                  Component: (await import("@/features/advice-portal/components/NewClientPage"))
                    .NewClientPage,
                }),
              },
              {
                path: "advice-portal/records/:family/:recordId",
                handle: title("shell.titles.advice"),
                lazy: async () => ({
                  Component: (await import("@/routes/RecordRoute")).RecordRoute,
                }),
              },
              {
                path: "order-instructions/:tab?",
                handle: { ...title("shell.titles.orders"), keepMountedAcrossSegments: true },
                lazy: async () => ({
                  Component: (
                    await import("@/features/order-instructions/components/OrderInstructionsPage")
                  ).OrderInstructionsPage,
                }),
              },
              {
                path: "quant-admin/:tab?",
                handle: {
                  titleKey: "shell.titles.quantAdmin",
                  split: true,
                  keepMountedAcrossSegments: true,
                } satisfies ShellRouteHandle,
                lazy: async () => ({
                  Component: (await import("@/features/quant-admin/components/QuantAdminPage"))
                    .QuantAdminPage,
                }),
              },
              {
                path: "admin/:section?",
                handle: {
                  titleKey: "shell.titles.admin",
                  split: true,
                  keepMountedAcrossSegments: true,
                } satisfies ShellRouteHandle,
                lazy: async () => {
                  const { AdminPage } = await import("@/features/admin/components/AdminPage");
                  return {
                    element: (
                      <DockGate entry="admin">
                        <AdminPage />
                      </DockGate>
                    ),
                  };
                },
              },
              {
                path: "*",
                lazy: async () => ({
                  Component: (await import("@/app/errors/NotFoundPage")).NotFoundPage,
                }),
              },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);

function useShellRouteHandle(): ShellRouteHandle {
  const matches = useMatches();
  const merged: ShellRouteHandle = {};
  for (const match of matches) {
    if (match.handle) Object.assign(merged, match.handle as ShellRouteHandle);
  }
  return merged;
}

function title(titleKey: string): ShellRouteHandle {
  return { titleKey };
}

function AppLayout() {
  const route = useShellRouteHandle();

  return (
    <SessionGate>
      <AdvisorGate>
        <TimeZoneProvider>
          <AppShell route={route}>
            <RouteTransition fallback={RouteErrorFallback}>
              <Outlet />
            </RouteTransition>
          </AppShell>
          <ScrollRestoration />
        </TimeZoneProvider>
      </AdvisorGate>
    </SessionGate>
  );
}
